import AddToCartButton from '@/components/AddToCartButton/AddToCartButton'
import T from '@/components/Helpers/Translate'
import { useGetCustomerProductPricing } from '@/lib/hooks/helpers/useGetCustomerProductPricing'
import { getProductRecommendationSlugArray } from '@/lib/utils/shared/getProductRecommendationSlugArray'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import type { CartItemType, CartType } from '@/store/types/cart'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { generateAddToCartEventData } from '@/lib/services/mixpanel/helpers/generateCartEventData'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'

type Props = {
  product: CosmicProductType
  index: number
}

const CartSheetRecommendedProductsCarouselItem = ({
  product,
  index,
}: Props) => {
  // We choose the first variation, the way this is currently set up,
  // we could allow the user to choose a variation from here as well.
  // WE have access on this object to all the variations with pricing for each of the products
  const variation = product.variations[0]

  const {
    user,
    addToCart,
    cart,
    app: { marketExceptions },
  } = useGlobalStore()
  const { displayPrice, getTerms } = useGetCustomerProductPricing()

  const { gtm } = useGTM()

  const handleAddToCart = () => {
    const itemToAddToCart: CartItemType = {
      subscriptionDiscount: product.subscriptionDiscount || 0,
      guarantee: product.policies?.[0] || null,
      slug: product.slug,
      title: product.name,
      image: product.imageUrl || '',
      purchaseOptions: product.purchaseOptions || null,
      description: product.description,
      po: 'otp',
      qty: 1,
      product: variation,
      recommendedProducts: getProductRecommendationSlugArray({
        productData: product,
      }),
      addToCartLocation: ['cart_you_may_also_like'],
    }
    addToCart(itemToAddToCart)
    gtm.addToCart({
      cartItem: itemToAddToCart,
      event_location: 'cart_you_may_also_like',
    })

    mixpanelService
      .trackEvent(MixpanelEvent.ADD_TO_CART, {
        event_location: 'cart_you_may_also_like',
        cart: generateAddToCartEventData({
          cartItem: itemToAddToCart,
          currency: marketExceptions?.currencyCode || 'USD',
          price:
            getTerms({
              forCustomerType: user.customerType,
              pricingData: variation.pricing || null,
            })?.priceEach || 0,
          quantity: 1,
          addToCartLocation: 'cart_you_may_also_like',
          focus: false,
        }),
        item_count: 1,
      })
      .catch(() => {})

    const cartType = 'otp'
    let updatedCart: CartType = {
      subscribePromo: [],
      otpPromo: [],
      otp: [],
      subscribe: [],
      addedLines: { items: [] },
      promoItems: [],
    }

    const newCartItem = {
      ...itemToAddToCart,
    }
    updatedCart = {
      ...cart,
      [cartType]: [...cart[cartType], newCartItem],
    }

    gtm.viewCart([...updatedCart.otp, ...updatedCart.subscribe])
  }

  return (
    <div className="flex flex-col items-center">
      <div className="my-3 flex w-full items-center justify-center overflow-hidden rounded-xl">
        <Image
          src={product.imageUrl || ''}
          alt={product.altText || product.name}
          className="h-full w-full max-w-72 object-contain"
          height={150}
          width={150}
        />
      </div>

      <div className="relative min-h-52 w-full">
        <h3 className="w-full text-lg font-bold text-primary">
          {product.name}
        </h3>
        <div
          className="line-clamp-6 text-sm text-primary"
          dangerouslySetInnerHTML={{ __html: product.description }}
        />
        <AddToCartButton
          size="sm"
          className="absolute bottom-0 bg-primary"
          onClick={() => handleAddToCart()}
          data-test={`add-to-cart-recommended-${index}`}
        >
          <div className="flex items-center justify-center space-x-2">
            <span>
              {displayPrice({
                forCustomerType: user.customerType,
                pricingData: variation.pricing,
              })}
            </span>
            <span>-</span>
            <T>add_to_cart</T>
          </div>
        </AddToCartButton>
      </div>
    </div>
  )
}

export default CartSheetRecommendedProductsCarouselItem
